import { Delete as BackspaceIcon, Keyboard as KeyboardIcon, Search, SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import {
  applyKeyboardKey, filterProjects, keyboardRows, searchProjects,
  type ProjectFilters
} from "../displayInteractions";
import type { Client, ProductType, ProjectStyle } from "../types";

const productOptions: Array<{ value: ProductType | null; label: string }> = [
  { value: null, label: "All" }, { value: "wood", label: "Wood" },
  { value: "aluminium", label: "Aluminium" }, { value: "upvc", label: "uPVC" },
  { value: "wood-aluminium", label: "Wood-aluminium" }
];
const styleOptions: Array<{ value: ProjectStyle | null; label: string }> = [
  { value: null, label: "All" }, { value: "modern", label: "Modern" },
  { value: "traditional", label: "Traditional" }, { value: "contemporary", label: "Contemporary" }
];

export function categoryLabel(value: string | null) {
  return value ? value.split(",").map((item) => item === "upvc" ? "uPVC" : item.charAt(0).toUpperCase() + item.slice(1)).join(" + ") : "Unassigned";
}

export function FilterPanel({
  filters, count, onChange, onClose
}: {
  filters: ProjectFilters; count: number;
  onChange: (filters: ProjectFilters) => void; onClose: () => void;
}) {
  return <section className="filter-panel floating-panel" aria-label="Filter projects">
    <header><div><SlidersHorizontal /><div><span className="eyebrow">Refine the map</span><h2>Filter projects</h2></div></div>
      <button onClick={onClose} aria-label="Close filters"><X /></button></header>
    <div className="filter-group"><strong>Product type</strong><div className="filter-chips">
      {productOptions.map((option) => <button key={option.label}
        className={filters.productType === option.value ? "active" : ""}
        aria-pressed={filters.productType === option.value}
        onClick={() => onChange({ ...filters, productType: option.value })}>{option.label}</button>)}
    </div></div>
    <div className="filter-group"><strong>Style</strong><div className="filter-chips">
      {styleOptions.map((option) => <button key={option.label}
        className={filters.style === option.value ? "active" : ""}
        aria-pressed={filters.style === option.value}
        onClick={() => onChange({ ...filters, style: option.value })}>{option.label}</button>)}
    </div></div>
    <footer><span><strong>{count}</strong> {count === 1 ? "project" : "projects"} found</span>
      <button className="clear-filter" onClick={() => onChange({ productType: null, style: null })}>Clear filters</button></footer>
  </section>;
}

function TouchKeyboard({
  onKey, onClose
}: {
  onKey: (key: string) => void; onClose: () => void;
}) {
  return <section className="touch-keyboard" aria-label="Floating on-screen keyboard">
    {keyboardRows.map((row, index) => <div className={"keyboard-row row-" + index} key={index}>
      {row.map((key) => <button key={key} onClick={() => onKey(key)}>{key}</button>)}
    </div>)}
    <div className="keyboard-row keyboard-actions">
      <button className="key-wide" onClick={() => onKey("Space")}>Space</button>
      <button onClick={() => onKey("Backspace")} aria-label="Backspace"><BackspaceIcon /></button>
      <button onClick={() => onKey("Clear")}>Clear</button>
      <button onClick={onClose}>Enter</button>
      <button className="key-wide" onClick={onClose}><KeyboardIcon /> Close keyboard</button>
    </div>
  </section>;
}

export function SearchOverlay({
  filteredClients, onSelect, onClose
}: {
  filteredClients: Client[];
  onSelect: (client: Client) => void; onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [keyboardVisible, setKeyboardVisible] = useState(true);
  const candidates = filteredClients;
  const hasQuery = query.trim().length > 0;
  const results = useMemo(() => hasQuery ? searchProjects(candidates, query) : [], [candidates, hasQuery, query]);
  const pressKey = (key: string) => setQuery((current) => applyKeyboardKey(current, key));

  return <section className={"search-overlay " + (keyboardVisible ? "keyboard-open" : "")} aria-label="Search projects">
    <header><div><span className="eyebrow">Find a client</span><h2>Search projects</h2></div>
    </header>
    <div className="search-input-row"><Search /><input value={query} readOnly inputMode="none"
      onFocus={() => setKeyboardVisible(true)} placeholder="Touch here to search by client name" aria-label="Client name" />
      {query && <button onClick={() => setQuery("")}>Clear</button>}
      <button onClick={onClose} aria-label="Close search"><X /></button></div>
    <div className="search-results" aria-live="polite">
      {results.length ? results.map((client) => {
        const cover = client.images.find((image) => image.id === client.coverImageId) || client.images[0];
        return <button className="search-result" key={client.id} onClick={() => onSelect(client)}>
          <span className="search-result-image">{cover ? <img src={cover.thumbnailPath} alt="" /> : <Search />}</span>
          <span className="search-result-copy"><strong>{client.name}</strong><small>{client.address || "Algarve"}</small>
            <span><i>{categoryLabel(client.productType)}</i><i>{categoryLabel(client.style)}</i></span></span>
        </button>;
      }) : <div className="search-empty"><Search /><strong>{hasQuery ? "No projects found" : "Start typing to search"}</strong></div>}
    </div>
    {keyboardVisible && <TouchKeyboard onKey={pressKey} onClose={() => setKeyboardVisible(false)} />}
    {!keyboardVisible && <button className="show-keyboard" onClick={() => setKeyboardVisible(true)}><KeyboardIcon /> Show keyboard</button>}
  </section>;
}

export function countFilteredProjects(clients: Client[], filters: ProjectFilters) {
  return filterProjects(clients, filters).length;
}
